import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { soccerPath } from '../utils/paths';
import './TeamCreation.css';

const TeamCreation = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [players, setPlayers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    creatorId: '',
    memberIds: [],
    numberOfTeams: 2,
    maxPlayersPerTeam: null,
  });
  const [loading, setLoading] = useState(false);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadPlayers = useCallback(async () => {
    try {
      setLoadingPlayers(true);
      const data = await api.getAllPlayers(user?.id);
      setPlayers(data);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load players');
    } finally {
      setLoadingPlayers(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadPlayers();
  }, [loadPlayers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'numberOfTeams' || name === 'maxPlayersPerTeam' 
        ? (value ? parseInt(value) : null) 
        : value,
    }));
    setError('');
    setSuccess('');
  };

  const handleCreatorChange = (e) => {
    const creatorId = e.target.value;
    setFormData((prev) => ({
      ...prev,
      creatorId,
      // Automatically add creator to members if not already selected
      memberIds: prev.memberIds.includes(creatorId)
        ? prev.memberIds
        : [...prev.memberIds, creatorId],
    }));
  };

  const handleMemberToggle = (playerId) => {
    setFormData((prev) => ({
      ...prev,
      memberIds: prev.memberIds.includes(playerId)
        ? prev.memberIds.filter((id) => id !== playerId)
        : [...prev.memberIds, playerId],
    }));
  };

  const handleSelectAll = () => {
    if (formData.memberIds.length === players.length) {
      setFormData((prev) => ({ ...prev, memberIds: [] }));
    } else {
      setFormData((prev) => ({
        ...prev,
        memberIds: players.map((p) => p.id),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.creatorId) {
      setError('Please select a team creator');
      return;
    }

    if (formData.memberIds.length < formData.numberOfTeams) {
      setError(`You need at least ${formData.numberOfTeams} players to divide into ${formData.numberOfTeams} teams`);
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const teamData = {
        name: formData.name,
        description: formData.description,
        creatorId: formData.creatorId,
        creatorUserId: user?.id || null, // Pass User ID for permission tracking
        memberIds: formData.memberIds,
        numberOfTeams: formData.numberOfTeams,
        maxPlayersPerTeam: formData.maxPlayersPerTeam,
      };

      const response = await api.createTeam(teamData, user?.id, isAdmin());
      setSuccess(`Team "${response.name}" created successfully!`);
      
      // Redirect to team view after 2 seconds
      setTimeout(() => {
        navigate(soccerPath(`/teams/${response.id}`));
      }, 2000);
    } catch (err) {
      setError(err.message || 'Failed to create team');
    } finally {
      setLoading(false);
    }
  };

  const getSkillBadgeClass = (skill) => {
    if (skill >= 8) return 'high';
    if (skill >= 5) return 'medium';
    return 'low';
  };

  const formatPosition = (position) => {
    return position.charAt(0) + position.slice(1).toLowerCase().replace('_', ' ');
  };

  if (loadingPlayers) {
    return <div className="loading">Loading players...</div>;
  }

  // Check if user has permission to create teams
  const hasCreatorRole = user?.roles && user.roles.includes('TEAM_CREATOR');
  const userIsAdmin = isAdmin();
  const canCreateTeam = userIsAdmin || hasCreatorRole;

  if (!canCreateTeam) {
    return (
      <div className="card">
        <h2>Create a Team</h2>
        <div className="alert alert-error">
          <strong>Access Restricted</strong>
          <p>Only team creators or admins can create teams.</p>
          <p>Please contact an admin to assign you the Team Creator role.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>Create a Team</h2>
      
      {error && (
        <div className="alert alert-error">{error}</div>
      )}
      
      {success && (
        <div className="alert alert-success">{success}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Team Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="e.g., Practice Session 1"
          />
        </div>

        <div className="form-group">
          <label htmlFor="description">Description</label>
          <input
            type="text"
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Optional team description"
          />
        </div>

        <div className="form-group">
          <label htmlFor="creatorId">Team Creator (You) *</label>
          <select
            id="creatorId"
            name="creatorId"
            value={formData.creatorId}
            onChange={handleCreatorChange}
            required
          >
            <option value="">Select yourself as creator</option>
            {players.map((player) => (
              <option key={player.id} value={player.id}>
                {player.name} ({formatPosition(player.position)}
                {user && (isAdmin() || (user.roles && user.roles.includes('TEAM_CREATOR'))) && `, Skill: ${player.skillLevel}/10`})
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>
            Select Team Members ({formData.memberIds.length} selected)
            <button
              type="button"
              onClick={handleSelectAll}
              className="btn btn-secondary"
              style={{ marginLeft: '1rem', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
            >
              {formData.memberIds.length === players.length ? 'Deselect All' : 'Select All'}
            </button>
          </label>
          <div className="checkbox-group">
            {players.map((player) => (
              <div
                key={player.id}
                className="checkbox-item"
              >
                <input
                  type="checkbox"
                  id={`player-${player.id}`}
                  checked={formData.memberIds.includes(player.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleMemberToggle(player.id);
                  }}
                />
                <label htmlFor={`player-${player.id}`}>
                  <strong>{player.name}</strong>
                  <br />
                  <small>
                    {formatPosition(player.position)}
                    {user && (isAdmin() || (user.roles && user.roles.includes('TEAM_CREATOR'))) && (
                      <>
                        {' • Skill: '}{player.skillLevel}/10
                        <span className={`skill-badge ${getSkillBadgeClass(player.skillLevel)}`} style={{ marginLeft: '0.5rem' }}>
                          {player.skillLevel}
                        </span>
                      </>
                    )}
                  </small>
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="numberOfTeams">Number of Teams to Divide Into *</label>
          <input
            type="number"
            id="numberOfTeams"
            name="numberOfTeams"
            min="2"
            max="10"
            value={formData.numberOfTeams}
            onChange={handleChange}
            required
          />
          <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
            The players will be automatically divided into balanced teams based on skill levels
          </small>
        </div>

        <div className="form-group">
          <label htmlFor="maxPlayersPerTeam">Max Players Per Team (Optional)</label>
          <input
            type="number"
            id="maxPlayersPerTeam"
            name="maxPlayersPerTeam"
            min="1"
            value={formData.maxPlayersPerTeam || ''}
            onChange={handleChange}
            placeholder="Leave empty for no limit"
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Creating Team...' : 'Create Team'}
        </button>
      </form>
    </div>
  );
};

export default TeamCreation;

